import cx from 'classnames'
import { MouseEvent, useState } from 'react'
import { Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import { openRequestOverlay } from '@js/common/request-flow-triggers-HDP'
import { UA } from '@js/common/UA'
import User from '@js/common/user'
import { formatRating } from '@js/common/utils'

import Icon from '@f/elements/Icon/Icon'

import { BauenIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/BauenIcon'
import { BungalowIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/BungalowIcon'
import { EinfamilienhausIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/EinfamilienhausIcon'
import { FertighausIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/FertighausIcon'
import { ImmonetIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/ImmonetIcon'
import { ImmoweltIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/ImmoweltIcon'
import { MassivhausIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/MassivhausIcon'
import { Nullable } from '@f-fertighaus/applications/Next/types/utility'
import { webtrack } from '@f-fertighaus/applications/Next/utils/webtrack'
import { useUserDetails } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseSections/sections/VendorDetailsSection/hooks/useUserDetails'
import { RatingCompact } from '@f-fertighaus/components/ReviewRating/SearchResultRating'

import { VendorSalesInfo } from './SalesInfo'

const BRANDING_PLAN = {
  pro: 'pro',
  premium: 'premium',
  basic: 'basic'
}

interface SearchResultHouseItemProps {
  nid: number
  slug: string
  name: string
  price_display: number
  living_area_total: number
  price_label: string
  vendor_verbose: any
  isTopPlacement: boolean
  isLocal: boolean
  images: any
  number_rooms: number
  utilization_name: string
  recently_added: Nullable<boolean>
}

interface SearchResultHouseCardProps {
  item: SearchResultHouseItemProps
  position: number
  hardLimit: any
  hardLimitOffset: number
  handleItemClick: (e: MouseEvent, houseNid?: string) => void
  redirectUrl: string
  imagesToRender: any[]
  openSetLocationModal: () => void
  disableBranding?: boolean
  variant: string
}

export const HouseCard = ({
  hardLimit,
  position,
  hardLimitOffset,
  item,
  redirectUrl,
  handleItemClick,
  imagesToRender,
  openSetLocationModal,
  variant,
  disableBranding = false
}: SearchResultHouseCardProps) => {
  const userDetails = useUserDetails({})
  const userLocation = User.getLocation()

  const [isFavourite, setIsFavourite] = useState(() => {
    return userDetails?.ua_favourites?.map(e => e.nid).includes(item.nid)
  })

  const brandingEnabled = !disableBranding

  const details = [
    {
      value: Math.round(item.living_area_total),
      label: 'm²',
      dataSel: 'txt-house-larea'
    },
    {
      value:
        item.number_rooms && item.number_rooms.toString().replace('.', ','),
      label: 'Zimmer',
      dataSel: 'txt-house-rooms'
    },
    {
      value: item.utilization_name,
      label: '',
      dataSel: 'txt-house-utilization'
    }
  ]

  const hasPremiumBrandingPlan =
    item.vendor_verbose.branding_plan === BRANDING_PLAN.premium
  const hasProBrandingPlan =
    item.vendor_verbose.branding_plan === BRANDING_PLAN.pro
  const hasProOrPremiumBrandingPlan =
    brandingEnabled && (hasProBrandingPlan || hasPremiumBrandingPlan)

  const showAttentionBar = brandingEnabled && hasProOrPremiumBrandingPlan
  const showCustomAttentionBar =
    brandingEnabled &&
    hasPremiumBrandingPlan &&
    item.vendor_verbose.branding_main_color

  const showThumbnails = hasPremiumBrandingPlan

  const showTopPlacementBadge = hasPremiumBrandingPlan
  const showTopLocalPlacementBadge =
    !showTopPlacementBadge &&
    userLocation?.postcode &&
    item.vendor_verbose.top_local_placement
  const placementBadge = [
    showTopPlacementBadge,
    showTopLocalPlacementBadge
  ].some(Boolean)
  const placementBadgeType = showTopPlacementBadge
    ? 'recommended'
    : 'topLocalPlacement'

  const showBadges =
    brandingEnabled &&
    [
      showTopPlacementBadge,
      showTopLocalPlacementBadge,
      item.recently_added
    ].some(Boolean)

  const thumbnailsToRender =
    imagesToRender.slice(1, 4).length < 3
      ? [
          ...imagesToRender.slice(1, 4),
          { ...imagesToRender[0], placeholder: true },
          { ...imagesToRender[1], placeholder: true }
        ].slice(0, 3)
      : imagesToRender.slice(1, 4)

  const {
    onAddToFavouriteClick,
    className: addToFavouriteClassname = '',
    ...restAddFavouriteConfig
  } = userDetails?.ua_user_state
    ? {
        'data-isfavorite': isFavourite,
        className: '',
        onAddToFavouriteClick: e => {
          e.stopPropagation()
          webtrack({
            category: 'CTA-button',
            action: 'click',
            label: 'favourite-house',
            location: 'image-gallery',
            values: [{ type: 'text', value: '(ICON)' }]
          })
          if (isFavourite) {
            UA.removeFavourite(item.nid.toString())
            setIsFavourite(false)
          } else {
            UA.addFavourite(item.nid.toString())
            setIsFavourite(true)
          }
        }
      }
    : {
        'data-request-type': 'house-wizard-item-favourite',
        'data-funnel-variant': 'house-wizard-item-favourite',
        'aria-label': 'Speichern',
        'data-sel': 'btn-fav',
        'data-house-nid': item.nid,
        className: 'js-request-button',
        ...(item.vendor_verbose.nid && {
          'data-vendor-nid': item.vendor_verbose.nid
        }),
        onAddToFavouriteClick: e => {
          e.stopPropagation()
          webtrack({
            category: 'CTA-button',
            action: 'click',
            label: 'favourite-house',
            location: 'image-tiles',
            indicates: ['funnel-entry'],
            values: [{ type: 'text', value: '(ICON)' }]
          })

          openRequestOverlay(e)
        }
      }

  const isEXLeadsPage = variant === 'ex_leads'
  const isNotEXLeadsPage = !isEXLeadsPage

  const handleClick = (e: MouseEvent) => {
    if (isEXLeadsPage) {
      handleItemClick(e, item.nid.toString())
      return
    }
    handleItemClick(e)
  }

  const Component = isEXLeadsPage ? 'button' : 'a'

  return (
    <Component
      {...(isNotEXLeadsPage && {
        href: redirectUrl
      })}
      onClick={handleClick}
      className={cx(
        'f-radius-card-large f-pos-rel f-m-a-020 HouseCard_wrapper f-!underline',
        hardLimit &&
          position > hardLimit.desktop + hardLimitOffset &&
          'f-hidden:l',
        hardLimit &&
          position > hardLimit.tablet + hardLimitOffset &&
          'f-hidden:m',
        hardLimit &&
          position > hardLimit.mobile + hardLimitOffset &&
          'f-hidden:s',
        (item.isTopPlacement || item.isLocal) && 'is-top-placement',
        item.isLocal && 'is-top-local-placement'
      )}
      data-sel={`${item.isTopPlacement ? 'crd-house-tp' : 'crd-house-dflt'}-${
        item.nid
      }`}>
      <div
        className={cx(
          'HouseCard_carousel-container',
          showThumbnails && 'HouseCard_carousel-container--with-thumbnails',
          showAttentionBar && 'HouseCard_carousel-container--attentionBar',
          showCustomAttentionBar &&
            'HouseCard_carousel-container--customAttentionBar'
        )}
        {...(showCustomAttentionBar && {
          style: {
            color: item.vendor_verbose.branding_main_color
          }
        })}>
        <Swiper
          modules={[Navigation, Pagination]}
          pagination={{
            el: '.Slider-counter',
            modifierClass: 'Slider-fraction-',
            type: 'fraction',
            renderFraction: (currentClass, totalClass) => {
              return `<span class='${currentClass}'></span> / <span class='${totalClass}'></span>`
            }
          }}
          spaceBetween={0}
          loop={true}
          slidesPerView={1}
          simulateTouch={false}
          watchOverflow={true}
          preventInteractionOnTransition={true}
          centerInsufficientSlides={true}
          navigation={{
            nextEl: '.SliderArrow-next',
            prevEl: '.SliderArrow-prev'
          }}>
          {imagesToRender.slice(0, 5).map((image, index) => {
            return (
              <SwiperSlide key={`item-picture-${item.slug}-${index}`}>
                <img
                  loading='lazy'
                  srcSet={`${image['fill-320x240']} 450w,
                    ${image['fill-640x480'] || image['fill-320x240']} 600w,
                    ${
                      image['fill-1280x960'] ||
                      image['fill-640x480'] ||
                      image['fill-320x240']
                    } 1000w
                    `}
                  sizes='640px'
                  src={`${image['fill-320x240']}`}
                  alt={image['alt_text']}
                  height='240'
                  width='320'
                  className='HouseCard_carousel-item-image'
                />
              </SwiperSlide>
            )
          })}
          <div
            onClick={e => {
              e.stopPropagation()
            }}
            className={`f-d-flc f-fl-al-ce f-fl-jc-ce f-c-ba-whi f-p-a-0 f-bo-round SliderArrow SliderArrow-prev`}>
            <Icon
              icon='left-link-16px'
              className='f-icon-030 f-c-st-bla f-icon-t-mini'
            />
            <span className='f-visually-hidden'>Vorheriges Bild</span>
          </div>
          <div
            onClick={e => {
              e.stopPropagation()
            }}
            className={`f-d-flc f-fl-al-ce f-fl-jc-ce f-c-ba-whi f-p-a-0 f-bo-round SliderArrow SliderArrow-next`}>
            <Icon
              icon='right-link-16px'
              className='f-icon-030 f-c-st-bla f-icon-t-mini'
            />
            <span className='f-visually-hidden'>Nächstes Bild</span>
          </div>
          <span className='Slider-counter f-t-micro f-c-fo-pri' />
          {!isEXLeadsPage && (
            <div
              {...restAddFavouriteConfig}
              data-sel='btn-fav-text'
              {...(item.vendor_verbose.nid && {
                'data-vendor-nid': item.vendor_verbose.nid
              })}
              onMouseEnter={() => {
                window.hdpPageProps = {
                  houseData: {
                    exterior_images: [
                      {
                        'max-1600x1200.watermarked':
                          item?.exterior_images[0]['max-1600x1200.watermarked'],
                        alt_text: item?.exterior_images[0]['alt_text']
                      }
                    ]
                  }
                }
              }}
              onClick={e => {
                e.preventDefault()
                e.stopPropagation()
                onAddToFavouriteClick(e)
              }}
              className={cx(
                'HouseCard_add-to-favourite-cta',
                addToFavouriteClassname
              )}>
              <Icon
                icon='heart-16px'
                className={cx(
                  'f-icon-030 f-c-st-whi f-icon-t-mini',
                  isFavourite ? 'f-c-fi-err' : 'f-c-fi-pri-050'
                )}
              />
            </div>
          )}
        </Swiper>
      </div>
      {showThumbnails && (
        <div className='f-d-flr f-fl-gap-005 f-m-t-005'>
          {thumbnailsToRender.map((image, index) => {
            if (image.hasOwnProperty('fill-320x240')) {
              return (
                <div
                  key={`item-picture-${item.slug}-${index}`}
                  className={'f-pos-rel HouseCard_thumbnail-item-wrapper'}>
                  <img
                    src={image['fill-320x240']}
                    alt={image['alt_text']}
                    height='90'
                    width='120'
                    className='HouseCard_thumbnail-item-image'
                  />
                  {image.placeholder && (
                    <div className='HouseCard_thumbnail-item-wrapper--placeholder'>
                      {{
                        fertighaus: () => <FertighausIcon />,
                        massivhaus: () => <MassivhausIcon />,
                        einfamilienhaus: () => <EinfamilienhausIcon />,
                        bungalow: () => <BungalowIcon />,
                        bauen: () => <BauenIcon />,
                        immowelt: () => <ImmoweltIcon />,
                        immonet: () => <ImmonetIcon />
                      }[window.__DOMAIN__]() || (() => null)()}
                    </div>
                  )}
                </div>
              )
            }
            return null
          })}
        </div>
      )}

      <div className='HouseCard_details-wrapper'>
        <div className='f-d-flc f-fl-al-st f-fl-gap-005'>
          <div
            className='f-t-mini f-c-fo-pri f-t-ellip'
            style={{ maxWidth: '270px' }}>
            {item.name}
          </div>
          <div>
            {item.price_display ? (
              <span
                className='f-d-inbl f-t-tminor f-c-fo-pri f-t-bold f-m-r-005'
                data-sel='txt-house-price'>
                {`ab ${item.price_display.toLocaleString('DE-de')} €`}
              </span>
            ) : (
              <span
                className='f-t-tminor f-c-fo-pri f-t-bold f-m-r-005'
                data-sel='txt-house-price'>
                Preis Auf Anfrage
              </span>
            )}

            <span className='f-t-micro f-c-fo-neu200'>{item.price_label}</span>
          </div>
          {showBadges && (
            <div className='f-d-flr f-fl-gap-005 f-fl-wrap'>
              {placementBadge && <PremiumBadge type={placementBadgeType} />}
              {item.recently_added && <PremiumBadge type={'new'} />}
            </div>
          )}
          <div
            style={{
              flex: '2'
            }}>
            {details
              .filter(d => d.value)
              .map((d, index, arr) => (
                <span key={index}>
                  <span
                    style={{ maxWidth: '280px' }}
                    className='f-t-mini f-c-fo-neu200'
                    data-sel={d.dataSel}>
                    {d.value} {d.label}
                  </span>
                  {index < arr.length - 1 && (
                    <>
                      <span className='f-m-h-005 f-c-fo-pri'>·</span>
                    </>
                  )}
                </span>
              ))}
          </div>
        </div>

        <div
          style={{
            flex: '1'
          }}
          className='f-d-flc f-fl-al-en f-fl-gap-010'>
          <div className='f-d-flr f-c-ba-whi f-p-a-005 HouseCard_vendor-logo '>
            <img
              src={item.vendor_verbose.logo?.medium}
              alt={item.vendor_verbose.display_name}
              className='SearchResults-item-house-vendor-logo'
            />
          </div>
          {item.vendor_verbose.reviews?.display_reviews &&
            window.__REVIEWS_ACTIVE__ && (
              <div className='HouseCard_vendor-rating-wrapper f-fl-gap-010'>
                <span className='f-c-fo-pri f-t-tbody f-no-shrink'>
                  {item.vendor_verbose.reviews.avg_rating > 0
                    ? formatRating(item.vendor_verbose.reviews.avg_rating)
                    : '-'}
                </span>
                <RatingCompact
                  value={item.vendor_verbose.reviews.avg_rating}
                  size={18}
                  style={{
                    strokeWidth: 0.5
                  }}
                />
                <span className='f-c-fo-neu200 f-t-micro f-no-shrink'>
                  ({item.vendor_verbose.reviews.count_total})
                </span>
              </div>
            )}
        </div>
      </div>
      {hasProOrPremiumBrandingPlan && (
        <div
          className='f-c-fo-pri'
          onClick={e => {
            e.stopPropagation()
            e.preventDefault()
          }}>
          <VendorSalesInfo
            nid={item.vendor_verbose.nid}
            postcode={userLocation.postcode}
            onLocationNotSetAction={openSetLocationModal}
            redirectUrl={redirectUrl}
          />
        </div>
      )}
    </Component>
  )
}

type PremiumBadgeType = 'topLocalPlacement' | 'new' | 'recommended'

interface PremiumBadgeProps {
  type: PremiumBadgeType
}
const PremiumBadge = ({ type }: PremiumBadgeProps) => {
  return (
    <>
      {{
        new: <NewHouseBadge />,
        topLocalPlacement: <TopLocalPlacementBadge />,
        recommended: <RecommendedVendorBadge />
      }[type] || null}
    </>
  )
}

interface PremiumBadgeComponentProps {
  bgColor?: string
  icon: string
  text: string
  iconClassname?: string
}

const PremiumBadgeComponent = ({
  bgColor,
  icon,
  iconClassname = 'f-c-fi-whi',
  text
}: PremiumBadgeComponentProps) => {
  return (
    <div
      className='f-d-flr f-fl-gap-005 f-c-fo-whi f-fl-al-ce f-c-ba-pri f-no-shrink'
      style={{
        ...(bgColor && { backgroundColor: bgColor }),
        padding: '2px 4px',
        borderRadius: '4px'
      }}>
      <Icon icon={icon} className={cx('f-icon-015', iconClassname)} />
      <span
        className='f-t-bold f-t-tbody'
        style={{
          fontSize: '10px',
          lineHeight: '1'
        }}>
        {text}
      </span>
    </div>
  )
}

const RecommendedVendorBadge = () => {
  return <PremiumBadgeComponent icon='star-16px' text='Empfohlener Anbieter' />
}

const TopLocalPlacementBadge = () => {
  return (
    <PremiumBadgeComponent
      bgColor='#1E9575'
      icon='pin-fill-16px'
      iconClassname='f-icon-015 f-c-fi-whi'
      text='Top Lokale Anbieter'
    />
  )
}

const NewHouseBadge = () => {
  return (
    <PremiumBadgeComponent
      bgColor='#5187C1'
      icon='lightbulb-16px-fill'
      iconClassname='f-c-fi-whi f-icon-015'
      text='Neues Haus'
    />
  )
}
